import { useQuery } from "@tanstack/react-query";
import { getAdminOverview } from "@/api/admin";
import { Card, SectionLabel } from "@/components/ui/Card";

function Tile({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: "default" | "accent" | "muted";
}) {
  const toneCls =
    tone === "accent"
      ? "text-amber-dark"
      : tone === "muted"
        ? "text-taupe"
        : "text-navy-deep";
  return (
    <Card className="flex flex-col gap-1 px-4 py-3">
      <span className="section-label">{label}</span>
      <span className={`metric-number text-[26px] ${toneCls}`}>{value}</span>
      {sub ? <span className="text-[11px] text-taupe">{sub}</span> : null}
    </Card>
  );
}

function usdFmt(n: number): string {
  if (n === 0) return "$0.00";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

export default function AdminOverview() {
  const q = useQuery({
    queryKey: ["admin-overview"],
    queryFn: getAdminOverview,
    refetchInterval: 60_000,
  });

  if (q.isLoading) {
    return <div className="py-6 text-center text-taupe">Loading…</div>;
  }
  if (!q.data) {
    return <div className="py-6 text-center text-coral">Failed to load.</div>;
  }

  const d = q.data;
  const spendDelta = d.ai_spend.prev_month_usd
    ? ((d.ai_spend.this_month_usd - d.ai_spend.prev_month_usd) /
        d.ai_spend.prev_month_usd) *
      100
    : null;

  return (
    <div className="space-y-6">
      <div>
        <SectionLabel>Users</SectionLabel>
        <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Tile label="Total" value={d.users.total} />
          <Tile label="New 30d" value={d.users.new_last_30d} />
          <Tile label="Active 7d" value={d.users.active_7d} />
          <Tile label="Admins" value={d.users.admins} tone="muted" />
        </div>
      </div>

      <div>
        <SectionLabel>Waitlist</SectionLabel>
        <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Tile label="Total" value={d.waitlist.total} />
          <Tile label="Confirmed" value={d.waitlist.confirmed} />
          <Tile label="New 7d" value={d.waitlist.new_last_7d} />
          <Tile
            label="Conversion"
            value={`${d.waitlist.convert_pct.toFixed(1)}%`}
            sub={`${d.waitlist.converted} signed up`}
          />
        </div>
      </div>

      <div>
        <SectionLabel>Claude spend</SectionLabel>
        <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Tile
            label="This month"
            value={usdFmt(d.ai_spend.this_month_usd)}
            sub={
              spendDelta != null
                ? `${spendDelta >= 0 ? "+" : ""}${spendDelta.toFixed(0)}% vs last month`
                : "no prior month yet"
            }
            tone="accent"
          />
          <Tile
            label="Last month"
            value={usdFmt(d.ai_spend.prev_month_usd)}
            tone="muted"
          />
          <Tile label="Calls today" value={d.ai_spend.calls_today} />
          <Tile label="MRR (tbd)" value="$0" tone="muted" />
        </div>
      </div>

      <div className="text-[11px] text-taupe">
        Updated {new Date(d.generated_at).toLocaleString()}
      </div>
    </div>
  );
}
